import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Droplet, FlaskConical } from 'lucide-react';
type Props = {
  className?: string;
  inverted?: boolean;
  size?: number; // px, controls overall scale
};
const wrap = 'inline-flex items-center gap-2 leading-none [font-feature-settings:"ss01"]';
/** V1 — Current wordmark: peptides + .ml in green */
export function LogoV1({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`inline-flex items-baseline group ${className}`} aria-label="peptides.ml" data-id="element-381">
      <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
      fontSize: size
    }} data-id="element-382">
        peptides
      </span>
      <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
      fontSize: size * 0.55,
      marginLeft: 2
    }} data-id="element-383">
        .ml
      </span>
    </Link>;
}
/** V2 — Drop mark + wordmark (drop icon prefix) */
export function LogoV2({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`${wrap} group ${className}`} aria-label="peptides.ml" data-id="element-384">
      <span className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground" style={{
      width: size * 1.1,
      height: size * 1.1
    }} data-id="element-385">
        <Droplet fill="currentColor" stroke="none" style={{
        width: size * 0.55,
        height: size * 0.55
      }} data-id="element-386" />
      </span>
      <span className="inline-flex items-baseline" data-id="element-387">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-388">
          peptides
        </span>
        <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
        fontSize: size * 0.55,
        marginLeft: 2
      }} data-id="element-389">
          .ml
        </span>
      </span>
    </Link>;
}
/** V3 — Circle .ml badge + wordmark */
export function LogoV3({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`${wrap} group ${className}`} aria-label="peptides.ml" data-id="element-390">
      <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
      fontSize: size
    }} data-id="element-391">
        peptides
      </span>
      <span className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground font-bold tracking-[-0.01em] [font-feature-settings:'tnum']" style={{
      fontSize: size * 0.5,
      height: size * 1.05,
      minWidth: size * 1.05,
      paddingLeft: size * 0.18,
      paddingRight: size * 0.18
    }} data-id="element-392">
        .ml
      </span>
    </Link>;
}
/** V4 — Underline accent (handwritten-style green underline) */
export function LogoV4({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`inline-block group relative ${className}`} aria-label="peptides.ml" style={{
    paddingBottom: size * 0.18
  }} data-id="element-393">
      <span className="inline-flex items-baseline" data-id="element-394">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-395">
          peptides
        </span>
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-396">
          .ml
        </span>
      </span>
      <svg className="absolute left-0 bottom-0 text-primary" width="100%" height={size * 0.18} viewBox="0 0 100 6" preserveAspectRatio="none" aria-hidden data-id="element-397">
        <path d="M 1 4 Q 25 1 50 3 T 99 2" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" data-id="element-398" />
      </svg>
    </Link>;
}
/** V5 — Stacked monogram: filled square with "p" + wordmark */
export function LogoV5({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`${wrap} group ${className}`} aria-label="peptides.ml" data-id="element-399">
      <span className="inline-flex items-center justify-center rounded-[6px] bg-foreground text-background font-bold tracking-[-0.04em] leading-none" style={{
      width: size * 1.2,
      height: size * 1.2,
      fontSize: size * 0.85
    }} data-id="element-400">
        p<span className="text-primary -ml-px" data-id="element-401">.</span>
      </span>
      <span className="inline-flex items-baseline" data-id="element-402">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-403">
          peptides
        </span>
        <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
        fontSize: size * 0.55,
        marginLeft: 2
      }} data-id="element-404">
          .ml
        </span>
      </span>
    </Link>;
}
/** V6 — Bracketed technical mark: peptides[.ml] in mono technical style */
export function LogoV6({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  const muted = inverted ? 'text-background/40' : 'text-muted-foreground';
  return <Link to="/" className={`inline-flex items-baseline group ${className}`} aria-label="peptides.ml" style={{
    fontFamily: 'Geist Mono, ui-monospace, monospace'
  }} data-id="element-405">
      <span className={`font-semibold tracking-[-0.01em] leading-none ${fg}`} style={{
      fontSize: size * 0.92
    }} data-id="element-406">
        peptides
      </span>
      <span className={`leading-none ${muted}`} style={{
      fontSize: size * 0.92,
      marginLeft: 1
    }} data-id="element-407">
        [
      </span>
      <span className="font-semibold text-primary leading-none" style={{
      fontSize: size * 0.92
    }} data-id="element-408">
        .ml
      </span>
      <span className={`leading-none ${muted}`} style={{
      fontSize: size * 0.92
    }} data-id="element-409">
        ]
      </span>
    </Link>;
}
/** V7 — Big primary dot/period emphasis */
export function LogoV7({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`inline-flex items-baseline group ${className}`} aria-label="peptides.ml" data-id="element-410">
      <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
      fontSize: size
    }} data-id="element-411">
        peptides
      </span>
      <span className="inline-block rounded-full bg-primary" style={{
      width: size * 0.28,
      height: size * 0.28,
      margin: `0 ${size * 0.1}px`,
      transform: 'translateY(2px)'
    }} aria-hidden data-id="element-412" />
      <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
      fontSize: size
    }} data-id="element-413">
        ml
      </span>
    </Link>;
}
/** V8 — Flask icon + wordmark with .ml separated */
export function LogoV8({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`${wrap} group ${className}`} aria-label="peptides.ml" data-id="element-414">
      <span className="relative inline-flex items-center justify-center rounded-md border-2 border-primary text-primary" style={{
      width: size * 1.15,
      height: size * 1.15
    }} data-id="element-415">
        <FlaskConical style={{
        width: size * 0.6,
        height: size * 0.6
      }} strokeWidth={2} data-id="element-416" />
      </span>
      <span className="inline-flex items-baseline" data-id="element-417">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-418">
          peptides
        </span>
        <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
        fontSize: size * 0.55,
        marginLeft: 2
      }} data-id="element-419">
          .ml
        </span>
      </span>
    </Link>;
}
/** V9 — Vial silhouette + wordmark — most on-brand since the vial IS the product */
export function LogoV9({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  const stroke = inverted ? 'currentColor' : 'currentColor';
  return <Link to="/" className={`${wrap} group ${className}`} aria-label="peptides.ml" data-id="element-420">
      {/* Vial mark — simplified silhouette with green cake */}
      <svg viewBox="0 0 14 22" style={{
      width: size * 0.7,
      height: size * 1.1
    }} className={fg} aria-hidden data-id="element-421">
        {/* cap */}
        <rect x="4" y="1" width="6" height="2.5" rx="0.4" fill="currentColor" data-id="element-422" />
        <rect x="5" y="0" width="4" height="1.2" rx="0.3" fill="currentColor" opacity="0.7" data-id="element-423" />
        {/* body */}
        <rect x="3" y="3.5" width="8" height="17" rx="0.8" fill="none" stroke="currentColor" strokeWidth="1.1" data-id="element-424" />
        {/* cake / lyo fill in primary green */}
        <rect x="4" y="15.5" width="6" height="4.5" rx="0.4" className="fill-primary" data-id="element-425" />
      </svg>
      <span className="inline-flex items-baseline" data-id="element-426">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-427">
          peptides
        </span>
        <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
        fontSize: size * 0.55,
        marginLeft: 2
      }} data-id="element-428">
          .ml
        </span>
      </span>
    </Link>;
}
/** V10 — Lab spec line style: 'PEPTIDES · ML' all-caps mono with green middle dot */
export function LogoV10({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`inline-flex items-center group ${className}`} aria-label="peptides.ml" style={{
    fontFamily: 'Geist Mono, ui-monospace, monospace'
  }} data-id="element-429">
      <span className={`font-semibold uppercase leading-none tracking-[0.06em] ${fg}`} style={{
      fontSize: size * 0.85
    }} data-id="element-430">
        PEPTIDES
      </span>
      <span className="inline-block rounded-full bg-primary mx-2" style={{
      width: size * 0.22,
      height: size * 0.22
    }} aria-hidden data-id="element-431" />
      <span className={`font-semibold uppercase leading-none tracking-[0.06em] ${fg}`} style={{
      fontSize: size * 0.85
    }} data-id="element-432">
        ML
      </span>
    </Link>;
}
/** V11 — Verified-check + wordmark — leverages the lab-verified motif used across the site */
export function LogoV11({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`${wrap} group ${className}`} aria-label="peptides.ml" data-id="element-433">
      <svg viewBox="0 0 22 22" style={{
      width: size * 1.1,
      height: size * 1.1
    }} aria-hidden data-id="element-434">
        <circle cx="11" cy="11" r="10" className="fill-primary" data-id="element-435" />
        <path d="M6.5 11.5 L9.5 14.5 L15.5 7.5" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" data-id="element-436" />
      </svg>
      <span className="inline-flex items-baseline" data-id="element-437">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-438">
          peptides
        </span>
        <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
        fontSize: size * 0.55,
        marginLeft: 2
      }} data-id="element-439">
          .ml
        </span>
      </span>
    </Link>;
}
/** V12 — Pill frame around the wordmark — mirrors the bg-card border-border pill UI pattern */
export function LogoV12({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  const border = inverted ? 'border-background/30' : 'border-border';
  return <Link to="/" className={`inline-flex items-center group ${className}`} aria-label="peptides.ml" data-id="element-440">
      <span className={`inline-flex items-baseline rounded-full border ${border} px-3.5`} style={{
      height: size * 1.5,
      gap: 0
    }} data-id="element-441">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size * 0.85
      }} data-id="element-442">
          peptides
        </span>
        <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
        fontSize: size * 0.5,
        marginLeft: 2
      }} data-id="element-443">
          .ml
        </span>
      </span>
    </Link>;
}
/** V13 — Cross-hair scientific mark + wordmark */
export function LogoV13({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`${wrap} group ${className}`} aria-label="peptides.ml" data-id="element-444">
      <svg viewBox="0 0 22 22" style={{
      width: size * 1.05,
      height: size * 1.05
    }} className="text-primary" aria-hidden data-id="element-445">
        <circle cx="11" cy="11" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" data-id="element-446" />
        <circle cx="11" cy="11" r="2.5" fill="currentColor" data-id="element-447" />
        <line x1="11" y1="1.5" x2="11" y2="4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-448" />
        <line x1="11" y1="17.5" x2="11" y2="20.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-449" />
        <line x1="1.5" y1="11" x2="4.5" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-450" />
        <line x1="17.5" y1="11" x2="20.5" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-451" />
      </svg>
      <span className="inline-flex items-baseline" data-id="element-452">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-453">
          peptides
        </span>
        <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
        fontSize: size * 0.55,
        marginLeft: 2
      }} data-id="element-454">
          .ml
        </span>
      </span>
    </Link>;
}
/** V14 — Spec-line format: 'PEPTIDES.ML / RG-24' batch-number style */
export function LogoV14({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  const muted = inverted ? 'text-background/50' : 'text-muted-foreground';
  return <Link to="/" className={`inline-flex items-center group ${className}`} aria-label="peptides.ml" style={{
    fontFamily: 'Geist Mono, ui-monospace, monospace'
  }} data-id="element-455">
      <span className={`font-semibold uppercase leading-none tracking-[0.04em] ${fg}`} style={{
      fontSize: size * 0.85
    }} data-id="element-456">
        PEPTIDES
      </span>
      <span className="text-primary font-semibold leading-none" style={{
      fontSize: size * 0.85,
      margin: '0 1px'
    }} data-id="element-457">
        .ML
      </span>
      <span className={`leading-none ${muted}`} style={{
      fontSize: size * 0.85,
      margin: '0 4px'
    }} data-id="element-458">
        /
      </span>
      <span className={`font-semibold uppercase leading-none tracking-[0.04em] ${fg}`} style={{
      fontSize: size * 0.7
    }} data-id="element-459">
        UK
      </span>
    </Link>;
}
/** V15 — Stacked: wordmark on top, ".ml lab" subline in green mono */
export function LogoV15({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`inline-flex flex-col group ${className}`} aria-label="peptides.ml" style={{
    lineHeight: 1
  }} data-id="element-460">
      <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
      fontSize: size
    }} data-id="element-461">
        peptides
      </span>
      <span className="font-mono font-semibold uppercase tracking-[0.12em] text-primary leading-none" style={{
      fontSize: size * 0.36,
      marginTop: size * 0.18
    }} data-id="element-462">
        .ml · uk lab
      </span>
    </Link>;
}
/** V16 — Inline "ml" superscript pill — keeps the wordmark intact but adds a green chip */
export function LogoV16({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`inline-flex items-start group ${className}`} aria-label="peptides.ml" data-id="element-463">
      <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
      fontSize: size
    }} data-id="element-464">
        peptides
      </span>
      <span className="font-mono font-bold tracking-tight text-primary-foreground bg-primary rounded-[3px] leading-none uppercase" style={{
      fontSize: size * 0.32,
      padding: `${size * 0.08}px ${size * 0.16}px`,
      marginLeft: size * 0.12,
      marginTop: -size * 0.05,
      letterSpacing: '0.05em'
    }} data-id="element-465">
        ml
      </span>
    </Link>;
}
/** V17 — Peptide chain mark — 4 connected dots referencing a polypeptide backbone */
export function LogoV17({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`${wrap} group ${className}`} aria-label="peptides.ml" data-id="element-466">
      <svg viewBox="0 0 36 12" style={{
      width: size * 1.5,
      height: size * 0.5
    }} aria-hidden data-id="element-467">
        {/* Backbone line */}
        <line x1="6" y1="6" x2="30" y2="6" stroke="currentColor" strokeWidth="1" className={fg} opacity="0.4" data-id="element-468" />
        {/* 4 amino acid residues — last one is primary green for the .ml */}
        <circle cx="6" cy="6" r="3" fill="currentColor" className={fg} data-id="element-469" />
        <circle cx="14" cy="6" r="3" fill="currentColor" className={fg} data-id="element-470" />
        <circle cx="22" cy="6" r="3" fill="currentColor" className={fg} data-id="element-471" />
        <circle cx="30" cy="6" r="3" className="fill-primary" data-id="element-472" />
      </svg>
      <span className="inline-flex items-baseline" data-id="element-473">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-474">
          peptides
        </span>
        <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
        fontSize: size * 0.55,
        marginLeft: 2
      }} data-id="element-475">
          .ml
        </span>
      </span>
    </Link>;
}
/** V18 — Lab stamp seal — circular sealed mark with "PEPTIDES.ML / UK LAB" curve */
export function LogoV18({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`inline-flex items-center group gap-3 ${className}`} aria-label="peptides.ml" data-id="element-476">
      <span className="relative inline-flex items-center justify-center" style={{
      width: size * 1.7,
      height: size * 1.7
    }} data-id="element-477">
        <svg viewBox="0 0 50 50" className="absolute inset-0 w-full h-full" aria-hidden data-id="element-478">
          {/* Outer + inner rings */}
          <circle cx="25" cy="25" r="23" fill="none" stroke="currentColor" strokeWidth="0.7" className={fg} data-id="element-479" />
          <circle cx="25" cy="25" r="19" fill="none" stroke="currentColor" strokeWidth="0.5" className={fg} opacity="0.4" data-id="element-480" />
          {/* Tick marks around outer ring */}
          {Array.from({
          length: 12
        }).map((_, i) => {
          const angle = i * 30 * Math.PI / 180;
          const x1 = 25 + 23 * Math.cos(angle);
          const y1 = 25 + 23 * Math.sin(angle);
          const x2 = 25 + 20.5 * Math.cos(angle);
          const y2 = 25 + 20.5 * Math.sin(angle);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth="0.6" className={fg} opacity="0.5" data-id="element-481" />;
        })}
          {/* Curved text around top */}
          <defs data-id="element-482">
            <path id={`curve-top-${bundleId(size)}`} d="M 8 25 A 17 17 0 0 1 42 25" fill="none" data-id="element-483" />
            <path id={`curve-bot-${bundleId(size)}`} d="M 9 28 A 16 16 0 0 0 41 28" fill="none" data-id="element-484" />
          </defs>
          <text fontFamily="Geist Mono, ui-monospace, monospace" fontSize="3.4" fontWeight="700" letterSpacing="0.8" className={fg} data-id="element-485">
            <textPath href={`#curve-top-${bundleId(size)}`} startOffset="50%" textAnchor="middle" data-id="element-486">
              PEPTIDES · ML
            </textPath>
          </text>
          <text fontFamily="Geist Mono, ui-monospace, monospace" fontSize="2.8" fontWeight="500" letterSpacing="1.2" className={fg} opacity="0.6" data-id="element-487">
            <textPath href={`#curve-bot-${bundleId(size)}`} startOffset="50%" textAnchor="middle" data-id="element-488">
              UK · RESEARCH GRADE
            </textPath>
          </text>
          {/* Center mark — primary green check */}
          <circle cx="25" cy="25" r="6.5" className="fill-primary" data-id="element-489" />
          <path d="M 21.5 25.5 L 24 28 L 28.5 22.5" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-490" />
        </svg>
      </span>
      <span className="inline-flex items-baseline" data-id="element-491">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-492">
          peptides
        </span>
        <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
        fontSize: size * 0.55,
        marginLeft: 2
      }} data-id="element-493">
          .ml
        </span>
      </span>
    </Link>;
}
/** V19 — Periodic table cell — element-box monogram styled like a periodic-table tile */
export function LogoV19({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  const bg = inverted ? 'bg-foreground' : 'bg-background';
  const border = inverted ? 'border-background/30' : 'border-foreground';
  return <Link to="/" className={`inline-flex items-center group gap-3 ${className}`} aria-label="peptides.ml" data-id="element-494">
      <span className={`relative inline-flex flex-col items-stretch justify-between ${bg} border ${border} rounded-md`} style={{
      width: size * 1.55,
      height: size * 1.55,
      padding: size * 0.18
    }} data-id="element-495">
        <span className="font-mono text-primary leading-none self-start" style={{
        fontSize: size * 0.32,
        fontWeight: 600
      }} data-id="element-496">
          ml
        </span>
        <span className={`${fg} font-semibold tracking-tight self-center leading-none`} style={{
        fontSize: size * 0.75,
        marginBottom: size * -0.04
      }} data-id="element-497">
          Pp
        </span>
        <span className="font-mono text-primary leading-none self-end" style={{
        fontSize: size * 0.3,
        fontWeight: 600
      }} data-id="element-498">
          .ml
        </span>
      </span>
      <span className="inline-flex items-baseline" data-id="element-499">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-500">
          peptides
        </span>
        <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
        fontSize: size * 0.55,
        marginLeft: 2
      }} data-id="element-501">
          .ml
        </span>
      </span>
    </Link>;
}
/** V20 — Slash divider — peptides | ml with a primary-green vertical bar */
export function LogoV20({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`inline-flex items-center group ${className}`} aria-label="peptides.ml" data-id="element-502">
      <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
      fontSize: size
    }} data-id="element-503">
        peptides
      </span>
      <span className="inline-block bg-primary rounded-full" style={{
      width: size * 0.1,
      height: size * 0.85,
      margin: `0 ${size * 0.28}px`,
      transform: 'skew(-15deg)'
    }} aria-hidden data-id="element-504" />
      <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
      fontSize: size
    }} data-id="element-505">
        ml
      </span>
    </Link>;
}
/** V21 — Equation notation — "≡ peptides.ml" with mathematical identity sign */
export function LogoV21({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`inline-flex items-baseline group ${className}`} aria-label="peptides.ml" data-id="element-506">
      <span className="text-primary font-mono font-medium leading-none" style={{
      fontSize: size * 1.05,
      marginRight: size * 0.25,
      letterSpacing: '-0.05em'
    }} aria-hidden data-id="element-507">
        ≡
      </span>
      <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
      fontSize: size
    }} data-id="element-508">
        peptides
      </span>
      <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
      fontSize: size * 0.55,
      marginLeft: 2
    }} data-id="element-509">
        .ml
      </span>
    </Link>;
}
/** V22 — Aperture / iris — geometric circular mark with green inner ring */
export function LogoV22({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`${wrap} group ${className}`} aria-label="peptides.ml" data-id="element-510">
      <svg viewBox="0 0 24 24" style={{
      width: size * 1.1,
      height: size * 1.1
    }} aria-hidden data-id="element-511">
        <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="1.4" className={fg} data-id="element-512" />
        {/* Six aperture blades */}
        {[0, 60, 120, 180, 240, 300].map(rot => <path key={rot} d="M 12 4 L 16 11 L 8 11 Z" className="fill-primary" transform={`rotate(${rot} 12 12)`} opacity="0.85" data-id="element-513" />)}
        <circle cx="12" cy="12" r="2.3" fill="currentColor" className={fg} data-id="element-514" />
      </svg>
      <span className="inline-flex items-baseline" data-id="element-515">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-516">
          peptides
        </span>
        <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
        fontSize: size * 0.55,
        marginLeft: 2
      }} data-id="element-517">
          .ml
        </span>
      </span>
    </Link>;
}
/** V23 — Vertical signature — wordmark on top with a green underline that ends in a dot */
export function LogoV23({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`inline-flex flex-col items-start group ${className}`} aria-label="peptides.ml" data-id="element-518">
      <span className="inline-flex items-baseline" data-id="element-519">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-520">
          peptides
        </span>
        <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
        fontSize: size * 0.55,
        marginLeft: 2
      }} data-id="element-521">
          .ml
        </span>
      </span>
      <span className="inline-flex items-center mt-1.5" style={{
      width: size * 4
    }} aria-hidden data-id="element-522">
        <span className="flex-1 h-px bg-primary" style={{
        height: 1.5
      }} data-id="element-523" />
        <span className="inline-block rounded-full bg-primary" style={{
        width: size * 0.18,
        height: size * 0.18,
        marginLeft: size * 0.1
      }} data-id="element-524" />
      </span>
    </Link>;
}
/** V24 — Hex-grid molecular — a small hexagon flanked by a green dot, then wordmark */
export function LogoV24({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? 'text-background' : 'text-foreground';
  return <Link to="/" className={`${wrap} group ${className}`} aria-label="peptides.ml" data-id="element-525">
      <svg viewBox="0 0 32 22" style={{
      width: size * 1.45,
      height: size
    }} aria-hidden data-id="element-526">
        {/* Hexagon (chemistry ring) */}
        <polygon points="11,2 19,2 23,11 19,20 11,20 7,11" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" className={fg} data-id="element-527" />
        {/* Inner "dotted" suggestion of bonds */}
        <circle cx="15" cy="8" r="1.2" fill="currentColor" className={fg} data-id="element-528" />
        <circle cx="13" cy="14" r="1.2" fill="currentColor" className={fg} data-id="element-529" />
        <circle cx="17" cy="14" r="1.2" fill="currentColor" className={fg} data-id="element-530" />
        {/* Adjacent green dot — the .ml */}
        <circle cx="29" cy="11" r="2.5" className="fill-primary" data-id="element-531" />
      </svg>
      <span className="inline-flex items-baseline" data-id="element-532">
        <span className={`font-semibold tracking-[-0.02em] leading-none ${fg}`} style={{
        fontSize: size
      }} data-id="element-533">
          peptides
        </span>
        <span className="font-semibold tracking-[0.01em] leading-none text-primary" style={{
        fontSize: size * 0.55,
        marginLeft: 2
      }} data-id="element-534">
          .ml
        </span>
      </span>
    </Link>;
}
/** Helper: stable ids for SVG defs (avoids duplicate-id collisions when V18 renders multiple times) */
function bundleId(size: number) {
  return `mp-logo-${Math.round(size)}-${Math.random().toString(36).slice(2, 7)}`;
}
// Convenience export of all variants for the preview page
export const LOGO_VARIANTS: Array<{
  id: string;
  name: string;
  description: string;
  Component: ComponentType<Props>;
}> = [{
  id: 'v1',
  name: 'V1 — Current wordmark',
  description: 'Baseline — peptides with .ml raised in primary green.',
  Component: LogoV1
}, {
  id: 'v2',
  name: 'V2 — Drop mark + wordmark',
  description: 'Filled circular drop icon prefix. Quietly references "every .ml".',
  Component: LogoV2
}, {
  id: 'v3',
  name: 'V3 — Circle .ml badge',
  description: 'The .ml lives in a primary-green pill — the strongest brand mark.',
  Component: LogoV3
}, {
  id: 'v4',
  name: 'V4 — Hand-drawn underline',
  description: 'Editorial accent under the wordmark. Less corporate, more crafted.',
  Component: LogoV4
}, {
  id: 'v5',
  name: 'V5 — Square monogram + wordmark',
  description: 'A dark "p." monogram alongside the full wordmark. Most "logo".',
  Component: LogoV5
}, {
  id: 'v6',
  name: 'V6 — Bracketed technical',
  description: 'Mono peptides[.ml] — scientific / lab-supplier voice.',
  Component: LogoV6
}, {
  id: 'v7',
  name: 'V7 — Big period emphasis',
  description: 'peptides · ml split by a confident primary green dot.',
  Component: LogoV7
}, {
  id: 'v8',
  name: 'V8 — Flask mark + wordmark',
  description: 'Outlined flask icon paired with the wordmark. On-brand sector cue.',
  Component: LogoV8
}, {
  id: 'v9',
  name: 'V9 — Vial mark + wordmark',
  description: 'Mini vial silhouette with a green cake — the product as the mark. Most literal brand fit.',
  Component: LogoV9
}, {
  id: 'v10',
  name: 'V10 — Spec-line mono',
  description: 'PEPTIDES · ML in mono caps, primary-green dot separator — matches the spec lines used throughout the site.',
  Component: LogoV10
}, {
  id: 'v11',
  name: 'V11 — Verified-check mark + wordmark',
  description: 'A primary-green verified-check disc references the lab-verified motif used across product cards.',
  Component: LogoV11
}, {
  id: 'v12',
  name: 'V12 — Pill frame',
  description: 'Wordmark sits inside a rounded-full border pill — mirrors the bg-card border-border chip pattern used across the UI.',
  Component: LogoV12
}, {
  id: 'v13',
  name: 'V13 — Scientific cross-hair mark',
  description: 'A primary-green cross-hair / target mark prefix — confident scientific cue without being clinical.',
  Component: LogoV13
}, {
  id: 'v14',
  name: 'V14 — Batch-number style',
  description: 'PEPTIDES.ML / UK in mono caps, like a batch identifier on a vial label.',
  Component: LogoV14
}, {
  id: 'v15',
  name: 'V15 — Stacked with subline',
  description: 'Wordmark with a small primary-green ".ml · uk lab" subline. Editorial masthead feel.',
  Component: LogoV15
}, {
  id: 'v16',
  name: 'V16 — Green "ml" superscript chip',
  description: 'Wordmark with a small primary-green rounded chip carrying "ml" — bold modern tag.',
  Component: LogoV16
}, {
  id: 'v17',
  name: 'V17 — Peptide chain mark',
  description: 'Four connected dots representing a polypeptide backbone — last residue in primary green for the ".ml". Literal molecular reference.',
  Component: LogoV17
}, {
  id: 'v18',
  name: 'V18 — Lab stamp seal',
  description: 'Circular sealed mark with curved text and tick marks — like an official UKAS lab stamp. Strong trust signal.',
  Component: LogoV18
}, {
  id: 'v19',
  name: 'V19 — Periodic table cell',
  description: 'A "Pp" element tile with ml subscripts top + bottom — research-lab visual language at its purest.',
  Component: LogoV19
}, {
  id: 'v20',
  name: 'V20 — Skewed slash divider',
  description: 'peptides | ml split by a primary-green skewed bar — fashion-magazine masthead feel.',
  Component: LogoV20
}, {
  id: 'v21',
  name: 'V21 — Identity equation',
  description: '"≡ peptides.ml" mathematical-identity prefix — confident scientific notation.',
  Component: LogoV21
}, {
  id: 'v22',
  name: 'V22 — Aperture mark',
  description: 'Six-bladed primary-green aperture inside an outlined ring — references lab instrumentation and focus.',
  Component: LogoV22
}, {
  id: 'v23',
  name: 'V23 — Underline signature',
  description: 'Wordmark followed by a primary-green hairline + endpoint dot — editorial signature.',
  Component: LogoV23
}, {
  id: 'v24',
  name: 'V24 — Hex molecular',
  description: 'A chemistry hexagon with three inner residue dots, followed by a primary-green ".ml" disc.',
  Component: LogoV24
}];